import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { conceptsService } from '@/shared/api/services/concepts.service';
// import type { Concept, ConceptAngle, AngleStoreResponse } from '@/types/concept';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { trackEvent, AnalyticsEvent } from '@/shared/lib/analytics';
import type { DeleteConflictStrategy } from '@/types/project-angle';

export function useConcepts(
  projectId: string | undefined,
  params?: { pageSize?: number }
) {
  return useQuery({
    queryKey: cacheKeys.concepts.list(projectId),
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required');
      const pageSize = params?.pageSize;
      return conceptsService.listConcepts(
        projectId,
        pageSize ? { pageSize } : undefined
      );
    },
    enabled: !!projectId,
  });
}

// Sentinel conceptId for the disabled-query key path. `cacheKeys.concepts.all`
// is the invalidation prefix for the whole namespace — registering a query
// directly under it makes the disabled query a sibling of every concept
// detail/list/angles query, so any prefix-invalidate hits it. A dedicated
// sentinel keeps the disabled-state cache slot isolated.
const DISABLED_CONCEPT_KEY = '__disabled__';

export function useConcept(
  projectId: string | undefined,
  conceptId: string | undefined
) {
  // queryFn must return the same envelope shape as the concept page query
  // (`getConceptWithIncludes`) — both share `cacheKeys.concepts.detail`,
  // and a shape mismatch crashes whichever consumer reads second. `select`
  // unwraps `.data` for header consumers without polluting the cache.
  return useQuery({
    queryKey: cacheKeys.concepts.detail(conceptId ?? DISABLED_CONCEPT_KEY),
    queryFn: async () => {
      if (!projectId || !conceptId)
        throw new Error('Project ID and Concept ID are required');
      return conceptsService.getConceptWithIncludes(projectId, conceptId);
    },
    enabled: !!projectId && !!conceptId,
    select: (response) => response.data,
  });
}

export function useCreateConcept() {
  const queryClient = useQueryClient();
  const tErrors = useTranslations('common.errors');

  return useMutation({
    mutationKey: ['concepts', 'create'],
    mutationFn: async ({
      projectId,
      mainAngleId,
      derivativeAngleIds,
    }: {
      projectId: string;
      mainAngleId: string;
      derivativeAngleIds?: string[];
    }) => {
      return conceptsService.createConceptWithAngles(
        projectId,
        mainAngleId,
        derivativeAngleIds ?? []
      );
    },
    onSuccess: (_data, variables) => {
      // Top of the render funnel: a main angle now exists and is awaiting its
      // first render. Mirrors Apple's ConceptAnglesView.createConcept and
      // Android's ConceptsRepository.createConceptWithAngles.
      trackEvent(AnalyticsEvent.ANGLE_UPLOAD_COMPLETED, { angle_kind: 'main' });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.list(variables.projectId),
      });
    },
    onError: (error) => {
      toast.error(
        error instanceof ApiClientError ? error.getFirstError() : tErrors('generic')
      );
    },
  });
}

export function useUpdateConcept() {
  const queryClient = useQueryClient();
  const tErrors = useTranslations('common.errors');

  return useMutation({
    mutationKey: ['concepts', 'update'],
    meta: { lockId: (vars: unknown) => (vars as { conceptId: string }).conceptId },
    mutationFn: async ({
      projectId,
      conceptId,
      updates,
    }: {
      projectId: string;
      conceptId: string;
      updates: {
        title?: string | null;
        description?: string | null;
        note?: string | null;
        is_favorite?: boolean;
      };
    }) => {
      return conceptsService.updateConcept(projectId, conceptId, updates);
    },
    onMutate: async ({ projectId: _projectId, conceptId, updates }) => {
      const detailKey = cacheKeys.concepts.detail(conceptId);

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: detailKey });

      // Snapshot the previous value
      const previousConcept = queryClient.getQueryData(detailKey);

      // Helper function to optimistically update cache data
      const updateCacheData = (old: unknown) => {
        if (!old || typeof old !== 'object' || !('data' in old)) return old;
        const oldData = old as {
          data: { attributes: Record<string, unknown> };
        };

        return {
          ...oldData,
          data: {
            ...oldData.data,
            attributes: {
              ...oldData.data.attributes,
              ...updates,
            },
          },
        };
      };

      // Optimistically update cache
      queryClient.setQueryData(detailKey, updateCacheData);

      // Return a context object with the snapshotted value
      return { previousConcept };
    },
    onError: (err, variables, context) => {
      // If mutation fails, use the context returned from onMutate to roll back cache
      const { conceptId } = variables;
      if (context?.previousConcept) {
        queryClient.setQueryData(
          cacheKeys.concepts.detail(conceptId),
          context.previousConcept
        );
      }
      toast.error(
        err instanceof ApiClientError ? err.getFirstError() : tErrors('generic')
      );
    },
    onSettled: (data, error, variables) => {
      const { projectId, conceptId } = variables;
      // Always refetch after error or success to ensure cache is correct
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.detail(conceptId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.list(projectId),
      });
    },
  });
}

export function useDeleteConcept() {
  const queryClient = useQueryClient();
  const t = useTranslations('concept');
  const tCommon = useTranslations('common');

  return useMutation({
    mutationKey: ['concepts', 'delete'],
    meta: { lockId: (vars: unknown) => (vars as { conceptId: string }).conceptId },
    mutationFn: async ({
      projectId,
      conceptId,
    }: {
      projectId: string;
      conceptId: string;
    }) => {
      return conceptsService.deleteConcept(projectId, conceptId);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.list(variables.projectId),
      });

      toast.undoable(t('conceptDeleted'), {
        action: {
          label: tCommon('undo'),
          onClick: async () => {
            try {
              await conceptsService.restoreConcept(
                variables.projectId,
                variables.conceptId
              );
            } catch {
              toast.error(t('failedToRestoreConcept'));
            }
            queryClient.invalidateQueries({
              queryKey: cacheKeys.concepts.list(variables.projectId),
            });
          },
        },
      });
    },
    onError: () => {
      toast.error(t('failedToDeleteConcept'));
    },
  });
}

export function useConceptAngles(conceptId: string | undefined) {
  return useQuery({
    queryKey: cacheKeys.concepts.angles(conceptId ?? DISABLED_CONCEPT_KEY),
    queryFn: async () => {
      if (!conceptId) throw new Error('Concept ID is required');
      return conceptsService.getConceptAngles(conceptId);
    },
    enabled: !!conceptId,
  });
}

/**
 * Delete a shared view from a single concept (the menu "Delete" on a view).
 *
 * Hits the dedicated concept-angle DELETE endpoint (not the angles/sync PUT) so
 * the server's two-phase conflict protocol works: when the view has renders in —
 * or is the main of — OTHER concepts the API answers 409 ANGLE_DELETE_CONFLICT
 * and the caller surfaces `AngleDeleteConflictDialog`, then retries with the
 * chosen `strategy`. Mirrors Apple/Android: explicit, dialog-confirmed removal,
 * no optimistic hide / Undo (the angle can survive elsewhere under
 * keep-rendered, so an Undo re-add would be wrong). The conflict 409 is
 * rethrown for the caller to catch — no generic toast here.
 */
export function useDeleteAngle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['concept_angles', 'delete'],
    meta: {
      lockId: (vars: unknown) => (vars as { angleId: string }).angleId,
    },
    mutationFn: async ({
      conceptId,
      conceptAngleId,
      strategy,
    }: {
      conceptId: string;
      // The concept_angle pivot id being removed from this concept.
      conceptAngleId: string;
      // The underlying project-angle id — used only as the cross-device
      // mutation lock key (the shared entity that may change elsewhere).
      angleId: string;
      // Phase 2: the conflict strategy the user picked in the dialog.
      strategy?: DeleteConflictStrategy;
    }) => {
      return conceptsService.deleteConceptAngle(
        conceptId,
        conceptAngleId,
        strategy
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.angles(variables.conceptId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.all,
      });
    },
  });
}
